/**
 * 基准面显示尺寸策略。
 *
 * 说明：
 * 1. 该文件只承载 SolidWorks 风格基准面显示尺寸的纯函数策略；
 * 2. 渲染对象创建仍由 DatumPlane / ReferencePlaneController 负责，避免策略层持有 Three.js 资源；
 * 3. 默认尺寸与自动尺寸统一从这里读取，防止不同入口重新退化为正方形。
 */

/** SolidWorks 创建基准面时相对参考几何的自动外扩比例。 */
export const REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE = 1.05;

/** 默认基准面显示宽高比；没有模型范围可参考时仍保持横向长方形。 */
export const DEFAULT_DATUM_PLANE_DISPLAY_ASPECT_RATIO = 4 / 3;

/**
 * 基准面显示尺寸。
 */
export interface DatumPlaneDisplaySize {
  /** 基准面显示宽度。 */
  width: number;
  /** 基准面显示高度。 */
  height: number;
}

/**
 * 判断数值是否为正向有限尺寸。
 *
 * @param value 待判断数值。
 * @returns 是有效尺寸时返回 true。
 */
export function isPositiveFiniteDisplaySize(value: number | null | undefined): value is number {
  return Number.isFinite(value) && Number(value) > 0;
}

/**
 * 解析缺省基准面显示尺寸。
 *
 * @param baseHeight 默认高度。
 * @returns 默认长方形显示尺寸。
 */
export function resolveDefaultDatumPlaneDisplaySize(baseHeight: number): DatumPlaneDisplaySize {
  const resolvedHeight = isPositiveFiniteDisplaySize(baseHeight) ? Number(baseHeight) : 1;
  return {
    width: resolvedHeight * DEFAULT_DATUM_PLANE_DISPLAY_ASPECT_RATIO,
    height: resolvedHeight,
  };
}

/**
 * 按 SolidWorks 自动尺寸规则外扩尺寸。
 *
 * @param size 原始参考尺寸。
 * @returns 外扩后的显示尺寸。
 */
export function applyReferencePlaneAutosizeMargin(size: number): number {
  return Number(size) * REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE;
}
